import { useState, useEffect, useMemo, useRef } from "react";
import { fetchAnalysisDetails, fetchAnalysisData } from "../../../Services/apiService";
import socketService from "../../../Services/socketService";
import { isCellFilled, createSnapshot } from "./analysisDataUtils";

const EMPTY_VALUES = () => Array(10).fill("");

// Consolidates the data-fetching + gating logic that used to live split across
// AnalysisDetails.js and AddAnalysisData.js (and used to travel between them via
// fragile router `state`). Everything here is keyed off orderId/testId only, so
// every tab works on direct URL entry / refresh, not just via in-app navigation.
export default function useWorkspaceData(orderId, testId) {
  // ---- formData (Analysis Details) ----
  const [formData, setFormData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState(null);

  const formDataRef = useRef(null);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!orderId || !testId) {
      setDetailsError("Missing order ID or test ID");
      setDetailsLoading(false);
      return;
    }

    let cancelled = false;

    const loadAnalysisDetails = async () => {
      try {
        setDetailsLoading(true);
        setDetailsError(null);

        const data = await fetchAnalysisDetails(orderId, testId);
        if (!data.submissionTimestamp) {
          data.submissionTimestamp = new Date().toISOString();
        }

        if (!cancelled) setFormData(data);
      } catch (err) {
        console.error("❌ Error loading analysis details:", err);
        if (!cancelled) setDetailsError(err.message);
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    loadAnalysisDetails();

    return () => {
      cancelled = true;
    };
  }, [orderId, testId]);

  const retryDetails = () => {
    if (!orderId || !testId) {
      setDetailsError("Missing order ID or test ID");
      return;
    }
    const loadAnalysisDetails = async () => {
      try {
        setDetailsLoading(true);
        setDetailsError(null);
        const data = await fetchAnalysisDetails(orderId, testId);
        setFormData(data);
      } catch (err) {
        console.error("❌ Error loading analysis details:", err);
        setDetailsError(err.message);
      } finally {
        setDetailsLoading(false);
      }
    };
    loadAnalysisDetails();
  };

  // ---- analysisData (Add Analysis Data) ----
  const [analysisData, setAnalysisData] = useState({
    h2_values: EMPTY_VALUES(),
    ch4_values: EMPTY_VALUES(),
    co2_values: EMPTY_VALUES(),
  });
  const [additionalInfo, setAdditionalInfo] = useState({
    date_evaluation: new Date().toISOString().split("T")[0],
    date_of_sampling: "",
    evaluated_by: "",
  });
  const [notes, setNotes] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [hasServerData, setHasServerData] = useState(false);
  const [savingAddData, setSavingAddData] = useState(false);
  const [addDataLoading, setAddDataLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);

  // Waits for the details fetch attempt to finish (success or fail) before
  // fetching analysis data, so the "date of sampling" default (derived from
  // formData.submissionTimestamp) is computed from real data rather than a race.
  useEffect(() => {
    if (!orderId || !testId) return;
    if (detailsLoading) return;

    let cancelled = false;

    const processAnalysisData = (existingData) => {
      if (!existingData) return;

      const actualData = existingData.data || existingData;

      const h2Values = EMPTY_VALUES();
      const ch4Values = EMPTY_VALUES();
      const co2Values = EMPTY_VALUES();

      if (actualData.analysis_data) {
        Object.keys(actualData.analysis_data).forEach((sampleKey) => {
          const sampleMatch = sampleKey.match(/sample_(\d+)/);
          if (sampleMatch) {
            const sampleNumber = parseInt(sampleMatch[1], 10);
            const sampleIndex = sampleNumber - 1;
            if (sampleIndex >= 0 && sampleIndex < 10) {
              const sampleData = actualData.analysis_data[sampleKey];
              h2Values[sampleIndex] =
                sampleData.h2 === "-" ? "-" : sampleData.h2?.toString() || "";
              ch4Values[sampleIndex] =
                sampleData.ch4 === "-" ? "-" : sampleData.ch4?.toString() || "";
              co2Values[sampleIndex] =
                sampleData.co2 === "-" ? "-" : sampleData.co2?.toString() || "";
            }
          }
        });
      }

      setAnalysisData({
        h2_values: h2Values,
        ch4_values: ch4Values,
        co2_values: co2Values,
      });

      let dateOfSampling = "";
      if (formDataRef.current?.submissionTimestamp) {
        dateOfSampling = new Date(formDataRef.current.submissionTimestamp)
          .toISOString()
          .split("T")[0];
      }

      let dateOfEvaluation = "";
      if (actualData?.evaluation_date) {
        dateOfEvaluation = new Date(actualData.evaluation_date)
          .toISOString()
          .split("T")[0];
      }

      const loadedAdditionalInfo = actualData.additional_info
        ? {
            date_evaluation: dateOfEvaluation || "",
            date_of_sampling: dateOfSampling,
            evaluated_by: actualData.additional_info.evaluated_by || "",
          }
        : {
            date_evaluation: dateOfEvaluation,
            date_of_sampling: dateOfSampling,
            evaluated_by: "",
          };

      setAdditionalInfo(loadedAdditionalInfo);

      const notesValue = actualData.notes || "";
      if (actualData.notes) {
        setNotes(notesValue);
      }

      setSavedSnapshot(
        createSnapshot(h2Values, ch4Values, co2Values, loadedAdditionalInfo, notesValue)
      );
      setHasServerData(true);
    };

    const buildEmptyDefaults = () => {
      const today = new Date().toISOString().split("T")[0];
      let formSubmissionDate = today;
      if (formDataRef.current?.submissionTimestamp) {
        formSubmissionDate = new Date(formDataRef.current.submissionTimestamp)
          .toISOString()
          .split("T")[0];
      }
      return {
        date_evaluation: today,
        date_of_sampling: formSubmissionDate,
        evaluated_by: "",
      };
    };

    const fetchExistingData = async () => {
      try {
        setAddDataLoading(true);
        const existingData = await fetchAnalysisData(orderId, testId);

        if (cancelled) return;

        if (existingData) {
          processAnalysisData(existingData);
        } else {
          const emptyAdditionalInfo = buildEmptyDefaults();
          setAdditionalInfo(emptyAdditionalInfo);
          setSavedSnapshot(
            createSnapshot(EMPTY_VALUES(), EMPTY_VALUES(), EMPTY_VALUES(), emptyAdditionalInfo, "")
          );
          setHasServerData(false);
        }
      } catch (error) {
        console.error("❌ Error fetching existing analysis data:", error);
        if (!cancelled) {
          const emptyAdditionalInfo = buildEmptyDefaults();
          setAdditionalInfo(emptyAdditionalInfo);
          setSavedSnapshot(
            createSnapshot(EMPTY_VALUES(), EMPTY_VALUES(), EMPTY_VALUES(), emptyAdditionalInfo, "")
          );
          setHasServerData(false);
        }
      } finally {
        if (!cancelled) setAddDataLoading(false);
      }
    };

    fetchExistingData();

    socketService.connect();

    const checkConnection = () => {
      setSocketConnected(socketService.isSocketConnected());
    };
    checkConnection();
    const connectionInterval = setInterval(checkConnection, 2000);

    const handleAnalysisDataUpdate = (socketData) => {
      if (socketData.order_id === orderId && socketData.test_id === testId) {
        processAnalysisData({ data: socketData });
      }
    };
    socketService.onAnalysisData(handleAnalysisDataUpdate);

    return () => {
      cancelled = true;
      clearInterval(connectionInterval);
      socketService.offAnalysisData();
    };
  }, [orderId, testId, detailsLoading]);

  // ---- derived gating flags ----
  const isTableComplete = useMemo(() => {
    for (let i = 0; i < 10; i++) {
      if (!isCellFilled(analysisData.h2_values[i])) return false;
      if (!isCellFilled(analysisData.ch4_values[i])) return false;
      if (!isCellFilled(analysisData.co2_values[i])) return false;
    }
    return true;
  }, [analysisData]);

  const currentSnapshot = useMemo(
    () =>
      createSnapshot(
        analysisData.h2_values,
        analysisData.ch4_values,
        analysisData.co2_values,
        additionalInfo,
        notes
      ),
    [analysisData, additionalInfo, notes]
  );

  const isDirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  const canSave = isTableComplete && isDirty && !savingAddData;
  const canCancel = isDirty && !savingAddData;
  const canGenerateReport = isTableComplete && !isDirty && hasServerData && !savingAddData;

  return {
    // details
    formData,
    setFormData,
    detailsLoading,
    detailsError,
    retryDetails,
    // add data
    analysisData,
    setAnalysisData,
    additionalInfo,
    setAdditionalInfo,
    notes,
    setNotes,
    savedSnapshot,
    setSavedSnapshot,
    hasServerData,
    setHasServerData,
    savingAddData,
    setSavingAddData,
    addDataLoading,
    socketConnected,
    // gating
    isTableComplete,
    currentSnapshot,
    isDirty,
    canSave,
    canCancel,
    canGenerateReport,
  };
}
